/**
 * Bilgi bankası state: the entry list (GET /kb), loaded when the screen opens; approve and
 * retire go to the server and the list is read again, so the page shows what the server
 * holds and never a guessed state.
 */

import { useSyncExternalStore } from 'react';
import type { TwinApi } from '../api/client.js';
import type { KbView } from '../api/kb.js';
import { useStore } from './context.js';

export interface KbState {
  view: KbView | null;
  error: string | null;
  loading: boolean;
  /** Entry ids with a decision in flight. */
  busy: readonly string[];
}

export class KbStore {
  private state: KbState = { view: null, error: null, loading: false, busy: [] };
  private readonly listeners = new Set<() => void>();
  private inFlight: Promise<void> | null = null;

  constructor(private readonly api: TwinApi) {}

  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  readonly getState = (): KbState => this.state;

  private set(patch: Partial<KbState>): void {
    this.state = { ...this.state, ...patch };
    for (const listener of this.listeners) listener();
  }

  ensureLoaded(): Promise<void> {
    if (this.state.view !== null || this.inFlight !== null) return this.inFlight ?? Promise.resolve();
    return this.reload();
  }

  reload(): Promise<void> {
    this.set({ loading: true });
    const run = (async () => {
      try {
        const view = await this.api.getKb();
        this.set({ view, error: null, loading: false });
      } catch (error) {
        this.set({ error: error instanceof Error ? error.message : String(error), loading: false });
      }
    })();
    this.inFlight = run;
    void run.finally(() => {
      if (this.inFlight === run) this.inFlight = null;
    });
    return run;
  }

  /** Approves a draft with the text as Sercan left it; resolves to the error text when refused. */
  async approve(id: string, input: { answerEn?: string; answerTr?: string }): Promise<string | null> {
    return this.decide(id, () => this.api.approveKb(id, input));
  }

  async retire(id: string): Promise<string | null> {
    return this.decide(id, () => this.api.retireKb(id));
  }

  private async decide(id: string, call: () => Promise<void>): Promise<string | null> {
    if (this.state.busy.includes(id)) return null;
    this.set({ busy: [...this.state.busy, id] });
    try {
      await call();
      await this.reload();
      return null;
    } catch (error) {
      return error instanceof Error ? error.message : String(error);
    } finally {
      this.set({ busy: this.state.busy.filter((item) => item !== id) });
    }
  }
}

export function useKb(): { store: KbStore; state: KbState } {
  const store = useStore().kb;
  const state = useSyncExternalStore(store.subscribe, store.getState, store.getState);
  return { store, state };
}
